import SupplierPdfActions from "./pdf-actions";
import { quoteErrorKey } from "../../supplier-pricing/quote-error";
import OrderPricingPanel from "../../supplier-pricing/order-pricing";
import { QuoteSnapshot } from "../../supplier-pricing/quote-document";
import { createOrderQuote } from "Services/supplier-pricing";
import toast from "react-hot-toast";
import { createSubmissionGate } from "../../tool-new/submission-gate";
import { ResponsiveOrderMenu } from "../anchored-order-menu";
import useMediaQuery from "../../../utils/hooks/use-media-query";
import { SupplierOrderCollection, SupplierOrderCard } from "./order-cards";
import { useTranslation as useInterfaceTranslation } from "react-i18next";
import { useEffect, useMemo, useState, useRef } from "react";
import { NavLink, useHistory } from "react-router-dom";
import { Modal, Spinner } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import infoIcon from "../../../assets/img/save_export/info.svg";
import previewIcon from "../../../assets/img/save_export/preview-dark.svg";
import editIcon from "../../../assets/img/save_export/edit-dark.svg";
import ticketIcon from "../../../assets/img/save_export/tickets.svg";
import deleteIcon from "../../../assets/img/save_export/delete-dark.svg";

import { useAppSelector } from "Redux/hooks";
import AddCustomerModal from "Components/supplier-customer-modal";
import { getOrders, patchOrder } from "Services/order";
import {
  deleteSupplierCustomer,
  getSupplierCustomers,
  updateSupplierCustomer,
} from "Services/supplier-customers";
import "./supplier.scss";

import { ActiveOrdersModal, CustomerDetailsModal } from "./customer-modals.tsx";

type OrderStatus = "NEW" | "QUOTE" | "IN_PRODUCTION" | "SENT";

const ORDER_STATUSES: OrderStatus[] = ["NEW", "QUOTE", "IN_PRODUCTION", "SENT"];

type CustomerStatus = "ACTIVE" | "NEW" | "INACTIVE";

const CUSTOMER_STATUSES: CustomerStatus[] = ["ACTIVE", "NEW", "INACTIVE"];
const getResults = (response: any) =>
  response?.data?.results || response?.data || [];

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="m16.5 16.5 4 4" />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <path d="m4 7 8 6 8-6" />
  </svg>
);

const DocumentIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <rect x="6" y="3" width="12" height="18" rx="2" />
    <path d="M9 8h6M9 12h6M9 16h4" />
  </svg>
);

type SupplierMyOrdersProps = {
  onOpenPreview: (orderId: number, download?: boolean, snapshot?: QuoteSnapshot) => void;
  onDeleteOrder: (order: any) => void;
  onSelectOrder: (order: any) => void;
  isDataFetched?: boolean;
  refreshKey?: number;
};

type SearchableFilterOption = {
  value: string;
  label: string;
};

type SearchableFilterProps = {
  value: string;
  options: SearchableFilterOption[];
  placeholder: string;
  emptyText: string;
  onChange: (value: string) => void;
  disabled?: boolean;
};

function SearchableFilter({
  value,
  options,
  placeholder,
  emptyText,
  onChange,
  disabled = false,
}: SearchableFilterProps) {
  const selectedOption = options.find((option) => option.value === value);

  const [search, setSearch] = useState(selectedOption?.label || "");

  const [open, setOpen] = useState(false);

  useEffect(() => {
    setSearch(selectedOption?.label || "");
  }, [value, selectedOption?.label]);

  const filteredOptions = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) {
      return options;
    }

    return options.filter((option) =>
      option.label.toLowerCase().includes(normalizedSearch),
    );
  }, [options, search]);

  const selectOption = (option: SearchableFilterOption) => {
    onChange(option.value);
    setSearch(option.label);
    setOpen(false);
  };

  const clearSelection = () => {
    onChange("");
    setSearch("");
    setOpen(false);
  };

  return (
    <div
      className={
        disabled
          ? "supplier-searchable-filter is-disabled"
          : "supplier-searchable-filter"
      }
    >
      <div className="supplier-searchable-filter__control">
        <input
          type="text"
          value={search}
          disabled={disabled}
          placeholder={placeholder}
          autoComplete="off"
          onFocus={() => setOpen(true)}
          onChange={(event) => {
            setSearch(event.target.value);
            setOpen(true);

            if (value) {
              onChange("");
            }
          }}
          onBlur={() => {
            window.setTimeout(() => {
              setOpen(false);

              if (!value) {
                setSearch("");
              }
            }, 150);
          }}
        />

        <button
          type="button"
          tabIndex={-1}
          disabled={disabled}
          aria-label={placeholder}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setOpen((current) => !current)}
        >
          <span aria-hidden="true" />
        </button>
      </div>

      {open && !disabled && (
        <div className="supplier-searchable-filter__menu">
          <button
            type="button"
            className={!value ? "is-selected" : ""}
            onMouseDown={(event) => event.preventDefault()}
            onClick={clearSelection}
          >
            {placeholder}
          </button>

          {filteredOptions.map((option) => (
            <button
              type="button"
              key={option.value}
              className={option.value === value ? "is-selected" : ""}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => selectOption(option)}
            >
              <span>{option.label}</span>

              {option.value === value && <span aria-hidden="true">✓</span>}
            </button>
          ))}

          {!filteredOptions.length && (
            <div className="supplier-searchable-filter__empty">{emptyText}</div>
          )}
        </div>
      )}
    </div>
  );
}

function SupplierMyOrders({
  onOpenPreview,
  onDeleteOrder,
  onSelectOrder,
  isDataFetched = false,
  refreshKey = 0,
}: SupplierMyOrdersProps) {
  const { t: pricingT } = useTranslation("supplierPricing");
  const [numberMenuId, setNumberMenuId] = useState<number | null>(null);
  const numberAnchor = useRef<HTMLElement | null>(null);
  const [pricingOrder, setPricingOrder] = useState<any>(null);
  const [pricingKey, setPricingKey] = useState("");
  const [pricingError, setPricingError] = useState("");
  const quoteFlight = useRef(false);
  const openPricedPdf = async (order: any) => {
    setNumberMenuId(null); setOpenOrderMenuId(null);
    setPricingError("");
    if (quoteFlight.current || order.is_creater === false) return;
    const key = order.price_list || "default";
    quoteFlight.current = true;
    try {
      const response = await createOrderQuote(order.id, key);
      const snapshot = response.data.snapshot as QuoteSnapshot;
      if (!snapshot || snapshot.order.id !== order.id || snapshot.price_list !== key) {
        throw new Error("Quote does not match saved selection");
      }
      onOpenPreview(order.id, false, snapshot);
    } catch (error) { const message = pricingT(quoteErrorKey(error)); setPricingError(message); toast.error(message); }
    finally { quoteFlight.current = false; }
  };
  const isMobile = useMediaQuery("(max-width: 991.98px)");
  const { t: uiT } = useInterfaceTranslation("interface");
  const { t } = useTranslation(["myOrder", "common", "newOrder"]);
  const rightToLeft = useAppSelector((state) => state.root.rightToLeft);
  const history = useHistory();

  const [incomingEditWarningOrder, setIncomingEditWarningOrder] =
    useState<any>(null);

  const sharedStatusGate = useRef(createSubmissionGate());
  const [updatingSharedOrderId, setUpdatingSharedOrderId] = useState<
    number | null
  >(null);

  const [copiedOrderId, setCopiedOrderId] = useState<number | null>(null);

  const handleCopyOrderNumber = async (order: any) => {
    if (!order?.order_no) return;

    try {
      await navigator.clipboard.writeText(order.order_no);
      setCopiedOrderId(order.id);

      window.setTimeout(() => {
        setCopiedOrderId((currentId) =>
          currentId === order.id ? null : currentId,
        );
      }, 1800);
    } catch (error) {
      console.error("Could not copy order number", error);
    }
  };

  const [orders, setOrders] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);

  const [ordersLoading, setOrdersLoading] = useState(true);
  const [customersLoading, setCustomersLoading] = useState(true);

  const [activeOrdersCustomer, setActiveOrdersCustomer] = useState<any>(null);

  const [detailsCustomer, setDetailsCustomer] = useState<any>(null);

  const [editingCustomer, setEditingCustomer] = useState<any>(null);

  const [openCustomerMenuId, setOpenCustomerMenuId] = useState<number | null>(
    null,
  );

  const [orderSearch, setOrderSearch] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");

  const [customerFilter, setCustomerFilter] = useState("");
  const [siteFilter, setSiteFilter] = useState("");
  const [deliverySort, setDeliverySort] = useState("");

  const [customerStatusFilter, setCustomerStatusFilter] = useState("");
  const [activityFilter, setActivityFilter] = useState("");

  const [openStatusId, setOpenStatusId] = useState<number | null>(null);

  const [hoveredOrderId, setHoveredOrderId] = useState<number | null>(null);

  const orderMenuAnchor = useRef<HTMLButtonElement | null>(null);
  const [openOrderMenuId, setOpenOrderMenuId] = useState<number | null>(null);
  const hoverCloseTimers = useRef<{ actions?: number; number?: number }>({});
  const cancelHoverClose = (kind: "actions" | "number") => {
    window.clearTimeout(hoverCloseTimers.current[kind]);
  };
  const scheduleHoverClose = (kind: "actions" | "number") => {
    cancelHoverClose(kind);
    hoverCloseTimers.current[kind] = window.setTimeout(() => {
      if (kind === "actions") setOpenOrderMenuId(null);
      else setNumberMenuId(null);
    }, 200);
  };
  useEffect(() => () => {
    Object.values(hoverCloseTimers.current).forEach(timer => window.clearTimeout(timer));
  }, []);


  const [openCustomerStatusId, setOpenCustomerStatusId] = useState<
    number | null
  >(null);

  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const [incomingOrderForCustomer, setIncomingOrderForCustomer] =
    useState<any>(null);

  const locale = rightToLeft ? "he-IL" : "en-GB";

  const formatDate = (date?: string) =>
    date ? new Date(date).toLocaleDateString(locale) : "-";

  const loadOrders = async () => {
    try {
      setOrdersLoading(true);

      const selectedSupplierSiteId = siteFilter.startsWith("supplier:")
        ? Number(siteFilter.replace("supplier:", ""))
        : undefined;

      const response = await getOrders({
        search: orderSearch || undefined,

        customer: customerFilter ? Number(customerFilter) : undefined,

        site: selectedSupplierSiteId,
      });

      setOrders(getResults(response));
    } finally {
      setOrdersLoading(false);
    }
  };

  const loadCustomers = async (showLoader = true) => {
    try {
      if (showLoader) {
        setCustomersLoading(true);
      }

      const response = await getSupplierCustomers(
        customerSearch,
        customerStatusFilter,
      );

      const refreshedCustomers = getResults(response);

      setCustomers(refreshedCustomers);

      return refreshedCustomers;
    } catch (error) {
      console.error("Could not load supplier customers", error);

      return null;
    } finally {
      if (showLoader) {
        setCustomersLoading(false);
      }
    }
  };

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (!target.closest(".supplier-order-actions, .mobile-order-actions-menu")) {
        setOpenOrderMenuId(null);
      }

      if (!target.closest(".supplier-order-number-pdf, .mobile-order-actions-menu")) {
        setNumberMenuId(null);
      }

      if (!target.closest(".supplier-status-dropdown")) {
        setOpenStatusId(null);
      }

      if (!target.closest(".supplier-customer-menu-wrapper")) {
        setOpenCustomerMenuId(null);
      }

      if (!target.closest(".supplier-customer-status-wrapper")) {
        setOpenCustomerStatusId(null);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    if (refreshKey > 0) {
      loadOrders();
    }
  }, [refreshKey]);

  useEffect(() => {
    loadOrders();
    loadCustomers();
  }, []);

  useEffect(() => {
    const timeout = window.setTimeout(loadOrders, 350);

    return () => window.clearTimeout(timeout);
  }, [orderSearch, customerFilter, siteFilter]);

  useEffect(() => {
    const timeout = window.setTimeout(loadCustomers, 350);

    return () => window.clearTimeout(timeout);
  }, [customerSearch, customerStatusFilter]);

  const customerFilterOptions = useMemo(
    () =>
      customers.map((customer: any) => ({
        value: String(customer.id),
        label: customer.name || "-",
      })),
    [customers],
  );

  const selectedFilterCustomer = useMemo(
    () =>
      customers.find((customer: any) => String(customer.id) === customerFilter),
    [customers, customerFilter],
  );

  const availableSites = useMemo(() => {
    const uniqueSites = new Map<string, SearchableFilterOption>();

    const sourceCustomers = selectedFilterCustomer
      ? [selectedFilterCustomer]
      : customers;

    // אתרים מהמודל החדש SupplierCustomerSite
    sourceCustomers.forEach((customer: any) => {
      (customer.sites || []).forEach((site: any) => {
        const value = `supplier:${site.id}`;

        uniqueSites.set(value, {
          value,
          label: selectedFilterCustomer
            ? site.name
            : `${site.name} — ${customer.name}`,
        });
      });
    });

    orders.forEach((order: any) => {
      const legacySiteName = order.site?.trim();

      if (!legacySiteName) {
        return;
      }

      if (
        selectedFilterCustomer &&
        String(order.supplier_customer?.id) !==
          String(selectedFilterCustomer.id)
      ) {
        return;
      }

      const value = `legacy:${legacySiteName}`;

      if (!uniqueSites.has(value)) {
        uniqueSites.set(value, {
          value,
          label: legacySiteName,
        });
      }
    });

    return Array.from(uniqueSites.values());
  }, [customers, orders, selectedFilterCustomer]);

  const displayedOrders = useMemo(() => {
    let filteredOrders = [...orders];

    if (siteFilter.startsWith("supplier:")) {
      const selectedSiteId = siteFilter.replace("supplier:", "");

      filteredOrders = filteredOrders.filter(
        (order: any) =>
          String(order.supplier_customer_site?.id) === selectedSiteId,
      );
    }

    if (siteFilter.startsWith("legacy:")) {
      const selectedLegacySite = siteFilter.replace("legacy:", "");

      filteredOrders = filteredOrders.filter(
        (order: any) => order.site?.trim() === selectedLegacySite,
      );
    }

    if (!deliverySort) {
      return filteredOrders;
    }

    return filteredOrders.sort((first, second) => {
      const firstDate = first.delivery_date
        ? new Date(first.delivery_date).getTime()
        : 0;

      const secondDate = second.delivery_date
        ? new Date(second.delivery_date).getTime()
        : 0;

      return deliverySort === "ASC"
        ? firstDate - secondDate
        : secondDate - firstDate;
    });
  }, [orders, siteFilter, deliverySort]);

  const displayedCustomers = useMemo(() => {
    if (!activityFilter) {
      return customers;
    }

    return customers.filter((customer) =>
      activityFilter === "ACTIVE"
        ? customer.active_orders_count > 0
        : customer.active_orders_count === 0,
    );
  }, [customers, activityFilter]);

  const updateOrderStatus = async (orderId: number, status: OrderStatus) => {
    const previousOrders = orders;

    setOrders((currentOrders) =>
      currentOrders.map((order) =>
        order.id === orderId
          ? {
              ...order,
              supplier_status: status,
            }
          : order,
      ),
    );

    setOpenStatusId(null);

    try {
      await patchOrder(orderId, {
        supplier_status: status,
      });
      const refreshedCustomers = await loadCustomers(false);

      if (!refreshedCustomers) {
        return;
      }

      setActiveOrdersCustomer((currentCustomer: any) => {
        if (!currentCustomer) {
          return null;
        }

        return (
          refreshedCustomers.find(
            (customer: any) => customer.id === currentCustomer.id,
          ) || currentCustomer
        );
      });
      setDetailsCustomer((currentCustomer: any) => {
        if (!currentCustomer) {
          return null;
        }

        return (
          refreshedCustomers.find(
            (customer: any) => customer.id === currentCustomer.id,
          ) || currentCustomer
        );
      });
    } catch (error) {
      setOrders(previousOrders);

      console.error("Could not update order status", error);
    }
  };

  const updateSharedOrderStatus = async (
    orderId: number,
    sharedStatus: "ACCEPTED" | "DECLINED",
  ) => {
    if (!sharedStatusGate.current.begin()) return;
    const notice = toast.loading(t("action updating"));
    const previousOrders = orders;

    setUpdatingSharedOrderId(orderId);
    setOpenOrderMenuId(null);

    if (sharedStatus === "DECLINED") {
      setOrders((currentOrders) =>
        currentOrders.filter((order) => order.id !== orderId),
      );
    } else {
      setOrders((currentOrders) =>
        currentOrders.map((order) =>
          order.id === orderId
            ? {
                ...order,
                shared_status: sharedStatus,
              }
            : order,
        ),
      );
    }

    try {
      await patchOrder(orderId, {
        shared_status: sharedStatus,
      });

      toast.success(t("action updated"), { id: notice });
      await loadCustomers(false);
    } catch (error: any) {
      setOrders(previousOrders);

      toast.error(t("action failed"), { id: notice });
    } finally {
      sharedStatusGate.current.end();
      setUpdatingSharedOrderId(null);
    }
  };

  const updateCustomerStatus = async (
    customerId: number,
    status: CustomerStatus,
  ) => {
    const previousCustomers = customers;

    setCustomers((currentCustomers) =>
      currentCustomers.map((customer) =>
        customer.id === customerId ? { ...customer, status } : customer,
      ),
    );

    setOpenCustomerStatusId(null);

    try {
      await updateSupplierCustomer(customerId, {
        status,
      });
    } catch (error) {
      setCustomers(previousCustomers);
      console.error("Could not update customer status", error);
    }
  };

  const incomingCustomerInitialValues = useMemo(() => {
    const incomingCustomer = incomingOrderForCustomer?.incoming_customer;

    if (!incomingCustomer) {
      return undefined;
    }

    return {
      name: incomingCustomer.name || "",
      business_number: incomingCustomer.business_number || "",
      contact_name: incomingCustomer.contact_name || "",
      phone: incomingCustomer.contact_phone || incomingCustomer.phone || "",
      email: incomingCustomer.contact_email || incomingCustomer.email || "",
      address: incomingCustomer.address || "",
      status: "ACTIVE",
    };
  }, [incomingOrderForCustomer]);

  const renderEditAction = (order: any) => <>
    <hr className="supplier-order-menu-separator" />
    <NavLink to={`/edit-order/${order.id}`} onClick={(event) => {
      setOpenOrderMenuId(null);
      setNumberMenuId(null);
      if (order.is_creater === false) {
        event.preventDefault();
        setIncomingEditWarningOrder(order);
      }
    }}>
      <img src={editIcon} alt="" aria-hidden="true" />
      <span>{t("edit", { ns: "common" })}</span>
    </NavLink>
  </>;

  return (
    <main className="supplier-dashboard" dir={rightToLeft ? "rtl" : "ltr"}>
      {pricingError && <p className="supplier-pdf-error" role="alert">{pricingError}</p>}
      <section className="supplier-dashboard__section">
        <h1 className="supplier-dashboard__title">
          {t("supplier orders title")}
        </h1>

        <div className="supplier-dashboard__search-row">
          <label className="supplier-search">
            <SearchIcon />
            <input
              value={orderSearch}
              onChange={(event) => setOrderSearch(event.target.value)}
              placeholder={t("supplier order search")}
            />
          </label>
        </div>

        <div className="supplier-dashboard__toolbar">
          <div className="supplier-dashboard__filters">
            <SearchableFilter
              value={customerFilter}
              options={customerFilterOptions}
              placeholder={t("customer filter all")}
              emptyText={t("no filter results")}
              onChange={(value) => {
                setCustomerFilter(value);
                setSiteFilter("");
              }}
            />

            <SearchableFilter
              value={siteFilter}
              options={availableSites}
              placeholder={t("project filter all")}
              emptyText={t("no filter results")}
              onChange={setSiteFilter}
            />

            <select
              value={deliverySort}
              onChange={(event) => setDeliverySort(event.target.value)}
            >
              <option value="">{t("sort delivery date")}</option>
              <option value="ASC">{t("sort oldest first")}</option>
              <option value="DESC">{t("sort newest first")}</option>
            </select>

            <NavLink to="/new-order" className="supplier-dark-button">
              <span>＋</span>
              {t("new supplier order")}
            </NavLink>
          </div>
        </div>

        <div className="supplier-table-shell">
          {ordersLoading ? (
            <div className="supplier-loader">
              <Spinner animation="border" />
            </div>
          ) : (
            <SupplierOrderCollection mobile={isMobile} className="supplier-table supplier-orders-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t("supplier created date")}</th>
                  <th>{t("supplier customer")}</th>
                  <th>{t("supplier order number")}</th>
                  <th>{t("supplier order name")}</th>
                  <th>{t("supplier contact person")}</th>
                  <th>{t("supplier total weight")}</th>
                  <th>{t("supplier delivery date")}</th>
                  <th>{t("supplier status")}</th>
                  <th />
                </tr>
              </thead>

              <tbody>
                {displayedOrders.map((order, index) => (
                  <SupplierOrderCard mobile={isMobile} order={order} siteLabel={t("site")}
                    key={order.id}
                    className={[
                      "supplier-order-row",

                      order.is_creater === false
                        ? "supplier-order-row--incoming"
                        : "",

                      order.is_creater === false &&
                      order.shared_status === "REQUESTED"
                        ? "supplier-order-row--pending"
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onMouseEnter={() => {
                      setHoveredOrderId(order.id);
                      onSelectOrder(order);
                    }}
                    onMouseLeave={() => {
                      setHoveredOrderId(null);

                      if (openOrderMenuId !== order.id) {
                        setOpenOrderMenuId(null);
                      }
                    }}
                  >
                    <td
                      data-label={t("order actions")}
                      className="supplier-order-actions-cell"
                      onMouseEnter={() => {
                        if (window.matchMedia("(hover: hover)").matches) {
                          cancelHoverClose("actions");
                          onSelectOrder(order);
                          setNumberMenuId(null);
                          setOpenOrderMenuId(order.id);
                        }
                      }}
                      onMouseLeave={() => {
                        if (window.matchMedia("(hover: hover)").matches) {
                          scheduleHoverClose("actions");
                        }
                      }}
                    >
                      {order.is_creater === false &&
                        order.shared_status === "REQUESTED" && (
                          <span
                            className="supplier-order-pending-indicator"
                            title={t("incoming order waiting")}
                          >
                            <i aria-hidden="true" />

                            <span>{t("incoming order waiting")}</span>
                          </span>
                        )}
                      {isMobile || hoveredOrderId === order.id ||
                      openOrderMenuId === order.id ? (
                        <div className="supplier-order-actions">
                          <button
                            type="button"
                            className={"supplier-order-menu-trigger" + (isMobile ? "" : " supplier-tooltip")}
                            data-tooltip={isMobile ? undefined : t("order actions")}
                            aria-label={t("order actions")}
                            aria-expanded={openOrderMenuId === order.id}
                            onClick={(event) => {
                              event.stopPropagation();
                              cancelHoverClose("actions");
                              orderMenuAnchor.current = event.currentTarget;
                              onSelectOrder(order);

                              setOpenOrderMenuId((currentId) =>
                                currentId === order.id ? null : order.id,
                              );
                            }}
                          >
                            <span aria-hidden="true">•••</span>
                          </button>

                          {openOrderMenuId === order.id && (
                            <ResponsiveOrderMenu mobile={isMobile} anchor={orderMenuAnchor.current} rtl={rightToLeft} label={t("order actions")} onClose={() => setOpenOrderMenuId(null)}>
                              {order.is_creater === false &&
                                order.shared_status === "REQUESTED" && (
                                  <>
                                    <button
                                      type="button"
                                      className="supplier-order-accept-action"
                                      disabled={
                                        updatingSharedOrderId !== null
                                      }
                                      onClick={() =>
                                        updateSharedOrderStatus(
                                          order.id,
                                          "ACCEPTED",
                                        )
                                      }
                                    >
                                      <span
                                        className="supplier-order-decision-icon"
                                        aria-hidden="true"
                                      >
                                        ✓
                                      </span>

                                      <span>{t("approve incoming order")}</span>
                                    </button>

                                    <button
                                      type="button"
                                      className="supplier-order-decline-action"
                                      disabled={
                                        updatingSharedOrderId !== null
                                      }
                                      onClick={() =>
                                        updateSharedOrderStatus(
                                          order.id,
                                          "DECLINED",
                                        )
                                      }
                                    >
                                      <span
                                        className="supplier-order-decision-icon"
                                        aria-hidden="true"
                                      >
                                        ×
                                      </span>

                                      <span>{t("decline incoming order")}</span>
                                    </button>
                                  </>
                                )}
                              <SupplierPdfActions order={order} busy={isDataFetched}
                                onUnpriced={(download) => { setOpenOrderMenuId(null); setNumberMenuId(null); onOpenPreview(order.id, download); }}
                                onPriced={() => openPricedPdf(order)} />

                              {renderEditAction(order)}

                              <NavLink
                                to={`/ticket-creator/?order_no=${order.order_no}`}
                                onClick={() => setOpenOrderMenuId(null)}
                              >
                                <img
                                  src={ticketIcon}
                                  alt=""
                                  aria-hidden="true"
                                />

                                <span>
                                  {t("print tickets", {
                                    ns: "common",
                                  })}
                                </span>
                              </NavLink>

                              <button
                                type="button"
                                className="supplier-order-delete-action"
                                onClick={() => {
                                  setOpenOrderMenuId(null);
                                  onSelectOrder(order);
                                  onDeleteOrder(order);
                                }}
                              >
                                <img
                                  src={deleteIcon}
                                  alt=""
                                  aria-hidden="true"
                                />

                                <span>
                                  {t("delete order", {
                                    ns: "common",
                                  })}
                                </span>
                              </button>
                            </ResponsiveOrderMenu>
                          )}
                        </div>
                      ) : (
                        <span className="supplier-order-row-number">
                          {index + 1}
                        </span>
                      )}
                    </td>

                    <td data-label={t("supplier created date")}>
                      {formatDate(order.created)}
                    </td>

                    <td
                      data-label={t("supplier customer")}
                      className="supplier-strong-cell"
                    >
                      {order.supplier_customer?.name ? (
                        <div className="supplier-order-customer">
                          <strong>{order.supplier_customer.name}</strong>

                          {order.supplier_customer.business_number && (
                            <small> {uiT("Business ID")} {order.supplier_customer.business_number}
                            </small>
                          )}
                        </div>
                      ) : order.is_creater === false &&
                        order.incoming_customer ? (
                        <div className="supplier-incoming-customer">
                          <div className="supplier-incoming-customer__details">
                            <strong>
                              {order.incoming_customer.name || "-"}
                            </strong>

                            {order.incoming_customer.business_number && (
                              <small> {uiT("Business ID")} {order.incoming_customer.business_number}
                              </small>
                            )}
                          </div>

                          <button
                            type="button"
                            className="supplier-incoming-customer__add"
                            onClick={(event) => {
                              event.stopPropagation();
                              setIncomingOrderForCustomer(order);
                              setAddCustomerOpen(true);
                            }}
                          >
                            <span aria-hidden="true">＋</span> {uiT("New")} </button>
                        </div>
                      ) : (
                        "-"
                      )}
                    </td>

                    <td data-label={t("supplier order number")}>
                      <div className="supplier-order-number supplier-order-number-pdf"
                        onMouseEnter={(event) => { if (isMobile) return; cancelHoverClose("number"); numberAnchor.current = event.currentTarget; setOpenOrderMenuId(null); setNumberMenuId(order.id); }}
                        onMouseLeave={() => { if (!isMobile) scheduleHoverClose("number"); }}
                      >
                        <button
                          type="button"
                          className="supplier-copy-badge supplier-tooltip"
                          data-tooltip={
                            copiedOrderId === order.id
                              ? t("order number copied", { ns: "myOrder" })
                              : t("copy order number", { ns: "myOrder" })
                          }
                          aria-label={t("copy order number", { ns: "myOrder" })}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleCopyOrderNumber(order);
                          }}
                        >
                          <span aria-hidden="true" />
                        </button>

                        <button type="button" className="supplier-order-number-trigger" dir="ltr"
                          aria-expanded={numberMenuId === order.id} aria-haspopup="menu"
                          onFocus={(event) => { cancelHoverClose("number"); numberAnchor.current = event.currentTarget; setOpenOrderMenuId(null); setNumberMenuId(order.id); }}
                          onClick={(event) => { event.stopPropagation(); cancelHoverClose("number"); numberAnchor.current = event.currentTarget; setOpenOrderMenuId(null); setNumberMenuId(order.id); }}
                        >{order.order_no || "-"}</button>
                        {numberMenuId === order.id && <ResponsiveOrderMenu mobile={isMobile}
                          anchor={numberAnchor.current} rtl={rightToLeft} label={t("order actions")}
                          onClose={() => setNumberMenuId(null)}>
                          <SupplierPdfActions order={order} busy={isDataFetched}
                            onUnpriced={(download) => { setNumberMenuId(null); setOpenOrderMenuId(null); onOpenPreview(order.id, download); }}
                            onPriced={() => openPricedPdf(order)} />
                          {renderEditAction(order)}
                        </ResponsiveOrderMenu>}
                      </div>
                    </td>

                    <td data-label={t("supplier order name")}>
                      {order.name || "-"}
                    </td>
                    <td data-label={t("supplier contact person")}>
                      <div className="supplier-order-contact">
                        <strong>
                          {order.creator_name ||
                            order.incoming_customer?.contact_name ||
                            "-"}
                        </strong>

                        {(order.creator_contact_number ||
                          order.incoming_customer?.contact_phone) && (
                          <small dir="ltr">
                            {order.creator_contact_number ||
                              order.incoming_customer?.contact_phone}
                          </small>
                        )}
                      </div>
                    </td>
                    <td data-label={t("supplier total weight")}>
                      {(Number(order.weight || 0) / 1000).toFixed(2)}
                    </td>
                    <td data-label={t("supplier delivery date")}>
                      {formatDate(order.delivery_date)}
                    </td>
                    <td
                      data-label={t("supplier status")}
                      className={
                        openStatusId === order.id
                          ? "supplier-status-cell is-open"
                          : "supplier-status-cell"
                      }
                    >
                      <div className="supplier-status-dropdown">
                        <button
                          type="button"
                          className={
                            "supplier-status-pill " +
                            `supplier-status-pill--${
                              order.supplier_status || "NEW"
                            }`
                          }
                          aria-expanded={openStatusId === order.id}
                          onClick={(event) => {
                            event.stopPropagation();

                            setOpenStatusId((currentId) =>
                              currentId === order.id ? null : order.id,
                            );
                          }}
                        >
                          {t(
                            `supplier status ${order.supplier_status || "NEW"}`,
                          )}

                          <span
                            className="supplier-status-arrow"
                            aria-hidden="true"
                          />
                        </button>

                        {openStatusId === order.id && (
                          <div
                            className="supplier-status-menu"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <strong>{t("update order status")}</strong>

                            {ORDER_STATUSES.map((status) => (
                              <button
                                type="button"
                                key={status}
                                className={
                                  order.supplier_status === status
                                    ? "is-selected"
                                    : ""
                                }
                                onClick={() =>
                                  updateOrderStatus(order.id, status)
                                }
                              >
                                <span
                                  className={
                                    "supplier-status-dot " +
                                    `supplier-status-dot--${status}`
                                  }
                                />

                                {t(`supplier status ${status}`)}

                                {order.supplier_status === status && (
                                  <span className="supplier-status-check">
                                    ✓
                                  </span>
                                )}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td data-label={t("Preview", { ns: "common" })}>
                      <button
                        type="button"
                        className="supplier-document-button supplier-tooltip"
                        data-tooltip={t("Preview", { ns: "common" })}
                        aria-label={t("Preview", { ns: "common" })}
                        disabled={isDataFetched}
                        onClick={(event) => {
                          event.stopPropagation();
                          onSelectOrder(order);
                          onOpenPreview(order.id);
                        }}
                      >
                        <DocumentIcon />
                      </button>
                    </td>
                  </SupplierOrderCard>
                ))}

                {!displayedOrders.length && (
                  <SupplierOrderCard mobile={isMobile}>
                    <td colSpan={10} className="supplier-empty-row">
                      {t("no supplier orders")}
                    </td>
                  </SupplierOrderCard>
                )}
              </tbody>
            </SupplierOrderCollection>
          )}
        </div>
      </section>

      <section className="supplier-dashboard__section supplier-dashboard__customers">
        <h2 className="supplier-dashboard__title">
          {t("supplier customers title")}
        </h2>

        <div className="supplier-dashboard__search-row">
          <label className="supplier-search">
            <SearchIcon />
            <input
              value={customerSearch}
              onChange={(event) => setCustomerSearch(event.target.value)}
              placeholder={t("supplier customer search")}
            />
          </label>
        </div>

        <div className="supplier-dashboard__toolbar">
          <div className="supplier-dashboard__filters">
            <select
              value={customerStatusFilter}
              onChange={(event) => setCustomerStatusFilter(event.target.value)}
            >
              <option value="">{t("customer status all")}</option>

              {CUSTOMER_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {t(`customer status ${status}`)}
                </option>
              ))}
            </select>

            <select
              value={activityFilter}
              onChange={(event) => setActivityFilter(event.target.value)}
            >
              <option value="">{t("activity filter all")}</option>
              <option value="ACTIVE">{t("activity filter active")}</option>
              <option value="NONE">{t("activity filter none")}</option>
            </select>

            <button
              type="button"
              className="supplier-dark-button"
              onClick={() => setAddCustomerOpen(true)}
            >
              <span>＋</span>
              {t("add supplier customer")}
            </button>
          </div>
        </div>

        <div className="supplier-table-shell">
          {customersLoading ? (
            <div className="supplier-loader">
              <Spinner animation="border" />
            </div>
          ) : (
            <table className="supplier-table supplier-customers-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t("supplier customer name")}</th>
                  <th>{t("supplier business number")}</th>
                  <th>{t("supplier contact person")}</th>
                  <th>{t("supplier phone")}</th>
                  <th>{t("supplier active orders")}</th>
                  <th>{t("supplier customer status")}</th>
                  <th>{t("supplier actions")}</th>
                </tr>
              </thead>

              <tbody>
                {displayedCustomers.map((customer, index) => {
                  const primaryContact = customer.contacts?.find(
                    (contact: any) => contact.is_primary,
                  );

                  const initials = customer.name
                    .split(" ")
                    .slice(0, 2)
                    .map((word: string) => word.charAt(0))
                    .join("");

                  return (
                    <tr key={customer.id}>
                      <td data-label="#">{index + 1}</td>

                      <td data-label={t("supplier customer name")}>
                        <div className="supplier-customer-name">
                          <span className="supplier-customer-avatar">
                            {initials}
                          </span>
                          <strong>{customer.name}</strong>
                        </div>
                      </td>

                      <td data-label={t("supplier business number")}>
                        {customer.business_number}
                      </td>

                      <td data-label={t("supplier contact person")}>
                        {primaryContact?.name || "-"}
                      </td>

                      <td data-label={t("supplier phone")}>
                        {customer.phone || "-"}
                      </td>

                      <td data-label={t("supplier active orders")}>
                        <button
                          type="button"
                          className="supplier-active-orders supplier-tooltip"
                          data-tooltip={t("show active orders")}
                          aria-label={t("show active orders")}
                          onClick={() => setActiveOrdersCustomer(customer)}
                        >
                          <strong>
                            {customer.active_orders_count}{" "}
                            {t("active orders count")}
                          </strong>

                          <span className="supplier-activity-dots">
                            {Array.from({
                              length: Math.min(customer.active_orders_count, 5),
                            }).map((_, dotIndex) => (
                              <i
                                key={dotIndex}
                                className={
                                  dotIndex === 0 ? "is-warning" : "is-active"
                                }
                              />
                            ))}
                          </span>

                          <span>‹</span>
                        </button>
                      </td>

                      <td
                        data-label={t("supplier customer status")}
                        className={
                          openCustomerStatusId === customer.id
                            ? "supplier-status-cell is-open"
                            : "supplier-status-cell"
                        }
                      >
                        <div className="supplier-status-dropdown supplier-customer-status-wrapper">
                          <button
                            type="button"
                            className={
                              "supplier-customer-status supplier-customer-status-button " +
                              `supplier-customer-status--${customer.status || "NEW"}`
                            }
                            aria-expanded={openCustomerStatusId === customer.id}
                            onClick={(event) => {
                              event.stopPropagation();

                              setOpenCustomerStatusId(
                                openCustomerStatusId === customer.id
                                  ? null
                                  : customer.id,
                              );
                            }}
                          >
                            {t(`customer status ${customer.status || "NEW"}`)}

                            <span
                              className="supplier-status-arrow"
                              aria-hidden="true"
                            />
                          </button>

                          {openCustomerStatusId === customer.id && (
                            <div className="supplier-status-menu supplier-customer-status-menu">
                              <strong>{t("update customer status")}</strong>

                              {CUSTOMER_STATUSES.map((status) => (
                                <button
                                  type="button"
                                  key={status}
                                  className={
                                    customer.status === status
                                      ? "is-selected"
                                      : ""
                                  }
                                  onClick={(event) => {
                                    event.stopPropagation();

                                    updateCustomerStatus(customer.id, status);
                                  }}
                                >
                                  <span
                                    className={
                                      "supplier-status-dot " +
                                      `supplier-customer-status-dot--${status}`
                                    }
                                  />

                                  {t(`customer status ${status}`)}

                                  {customer.status === status && (
                                    <span className="supplier-status-check">
                                      ✓
                                    </span>
                                  )}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>

                      <td data-label={t("supplier actions")}>
                        <div className="supplier-row-actions">
                          <a
                            href={
                              customer.email
                                ? `mailto:${customer.email}`
                                : undefined
                            }
                            className="supplier-mail-button supplier-tooltip"
                            data-tooltip={t("send customer email")}
                            aria-label={t("send customer email")}
                          >
                            <MailIcon />
                            {t("supplier email action")}
                          </a>

                          <div className="supplier-customer-menu-wrapper">
                            <button
                              type="button"
                              className="supplier-more-button supplier-tooltip"
                              data-tooltip={t("open customer actions")}
                              aria-label={t("open customer actions")}
                              aria-expanded={openCustomerMenuId === customer.id}
                              onClick={(event) => {
                                event.stopPropagation();

                                setOpenCustomerMenuId(
                                  openCustomerMenuId === customer.id
                                    ? null
                                    : customer.id,
                                );
                              }}
                            >
                              <span aria-hidden="true">•••</span>
                            </button>

                            {openCustomerMenuId === customer.id && (
                              <div className="supplier-customer-actions-menu">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setDetailsCustomer(customer);
                                    setOpenCustomerMenuId(null);
                                  }}
                                >
                                  {t("view customer details")}
                                </button>

                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingCustomer(customer);
                                    setAddCustomerOpen(true);
                                    setOpenCustomerMenuId(null);
                                  }}
                                >
                                  {t("edit customer")}
                                </button>

                                <button
                                  type="button"
                                  className="is-danger"
                                  onClick={async () => {
                                    setOpenCustomerMenuId(null);

                                    if (
                                      !window.confirm(
                                        t("delete customer confirm"),
                                      )
                                    ) {
                                      return;
                                    }

                                    await deleteSupplierCustomer(customer.id);
                                    await loadCustomers();
                                  }}
                                >
                                  {t("delete customer")}
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  );
                })}

                {!displayedCustomers.length && (
                  <tr>
                    <td colSpan={8} className="supplier-empty-row">
                      {t("no supplier customers")}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {incomingEditWarningOrder && (
        <div
          className="supplier-warning-modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setIncomingEditWarningOrder(null);
            }
          }}
        >
          <div
            className="supplier-warning-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="supplier-edit-warning-title"
            dir={rightToLeft ? "rtl" : "ltr"}
          >
            <button
              type="button"
              className="supplier-warning-modal__close"
              aria-label={t("close", { ns: "common" })}
              onClick={() => setIncomingEditWarningOrder(null)}
            >
              ×
            </button>

            <div className="supplier-warning-modal__icon" aria-hidden="true">
              !
            </div>

            <h3 id="supplier-edit-warning-title">
              {t("incoming order edit warning title", {
                ns: "myOrder",
              })}
            </h3>

            <p>
              {t("incoming order edit warning", {
                ns: "myOrder",
              })}
            </p>

            <div className="supplier-warning-modal__actions">
              <button
                type="button"
                className="supplier-warning-modal__cancel"
                onClick={() => setIncomingEditWarningOrder(null)}
              >
                {t("cancel edit", {
                  ns: "myOrder",
                })}
              </button>

              <button
                type="button"
                className="supplier-warning-modal__confirm"
                onClick={() => {
                  const orderId = incomingEditWarningOrder.id;

                  setIncomingEditWarningOrder(null);
                  history.push(`/edit-order/${orderId}`);
                }}
              >
                {t("continue to edit", {
                  ns: "myOrder",
                })}
              </button>
            </div>
          </div>
        </div>
      )}
      <AddCustomerModal
        show={addCustomerOpen}
        customer={editingCustomer}
        initialValues={incomingCustomerInitialValues}
        onClose={() => {
          setAddCustomerOpen(false);
          setEditingCustomer(null);
          setIncomingOrderForCustomer(null);
        }}
        onCreated={async (createdCustomer) => {
          const createdCustomerData =
            createdCustomer?.data ||
            createdCustomer?.customer ||
            createdCustomer;

          const createdCustomerId = createdCustomerData?.id;

          setAddCustomerOpen(false);
          setEditingCustomer(null);

          try {
            if (incomingOrderForCustomer && createdCustomerId) {
              await patchOrder(incomingOrderForCustomer.id, {
                supplier_customer: createdCustomerId,
              });
            }
          } catch (error: any) {
            console.error(
              "Customer created but order linking failed:",
              error?.response?.data || error,
            );
          } finally {
            setIncomingOrderForCustomer(null);

            await Promise.all([loadCustomers(), loadOrders()]);
          }
        }}
      />
      {activeOrdersCustomer && (
        <ActiveOrdersModal
          customer={activeOrdersCustomer}
          onClose={() => setActiveOrdersCustomer(null)}
          onOpenDetails={() => {
            setDetailsCustomer(activeOrdersCustomer);
            setActiveOrdersCustomer(null);
          }}
        />
      )}

      {detailsCustomer && (
        <CustomerDetailsModal
          customer={detailsCustomer}
          onClose={() => setDetailsCustomer(null)}
          onEditCustomer={(customer) => {
            setEditingCustomer(customer);
            setDetailsCustomer(null);
            setAddCustomerOpen(true);
          }}
        />
      )}
      <Modal show={Boolean(pricingOrder)} onHide={() => setPricingOrder(null)} centered
        dialogClassName="eb-pr" key={pricingOrder?.id || "closed"}>
        <Modal.Header closeButton><Modal.Title>{pricingT("order.exportPricedPdf")}</Modal.Title></Modal.Header>
        <Modal.Body>{pricingOrder && <OrderPricingPanel orderId={pricingOrder.id}
          supplierCustomerId={typeof pricingOrder.supplier_customer === "object" ? pricingOrder.supplier_customer?.id : pricingOrder.supplier_customer}
          priceList={pricingKey} onPriceListChange={(key) => {
            setPricingKey(key);
            setOrders((rows) => rows.map((row) => row.id === pricingOrder.id ? { ...row, price_list: key } : row));
          }} onQuoteGenerated={(snapshot) => { setPricingOrder(null); onOpenPreview(snapshot.order.id, false, snapshot); }} />}</Modal.Body>
      </Modal>
    </main>
  );
}

export default SupplierMyOrders;
